// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Solar System mode (owner, 2026-11-04; docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md):
 * the bodies the orrery draws, in the order the lists show them.
 *
 * Radii are the publisher's table (celestialBodies.ts, IAU / NASA fact
 * sheets). The poles are the IAU WGCCRE 2015 north poles (J2000
 * equatorial right ascension and declination), so Uranus lies on its side
 * and Saturn's rings tilt as they do. Each body names the datasets Browse
 * keeps while it is the one being looked at (`family`: the body and its
 * moons that have rows), and the datasets a visit opens (`landing`, best
 * first; the Earth keeps whatever the user had).
 */

import { BODY_RADII_KM } from '../celestialBodies'

export type SolarBodyId =
  | 'sun'
  | 'mercury'
  | 'venus'
  | 'earth'
  | 'moon'
  | 'mars'
  | 'ceres'
  | 'jupiter'
  | 'saturn'
  | 'uranus'
  | 'neptune'
  | 'pluto'

export type SolarBodyKind = 'star' | 'planet' | 'dwarf' | 'moon'

export interface SolarBody {
  id: SolarBodyId
  kind: SolarBodyKind
  /** Mean radius, km. */
  radiusKm: number
  /** A plain colour for the marker, the label and a sphere without a picture (sRGB hex). */
  colour: number
  /** IAU north pole, J2000 equatorial, degrees. */
  poleRaDeg: number
  poleDecDeg: number
  /** The `celestialBody` names Browse keeps while this body is looked at. */
  family: readonly string[]
  /**
   * The datasets a visit opens, best first: the first one the account may
   * open wins (the Sun's live stream needs a plan; a library video stands
   * in). Empty for the Earth, which keeps whatever the user had.
   */
  landing: readonly string[]
  /** A library row whose picture dresses the sphere close up (an equirectangular image under ~4 MB), or null. */
  picture: string | null
}

const R = BODY_RADII_KM

export const SOLAR_BODIES: readonly SolarBody[] = [
  { id: 'sun', kind: 'star', radiusKm: R.sun, colour: 0xffd36b, poleRaDeg: 286.13, poleDecDeg: 63.87, family: ['sun'], landing: ['R2_DASH_5fe39ec3c744c7fc', 'LIB_568adf4ca0cf42db'], picture: null },
  { id: 'mercury', kind: 'planet', radiusKm: R.mercury, colour: 0xa8a39c, poleRaDeg: 281.01, poleDecDeg: 61.42, family: ['mercury'], landing: ['LIB_b3122ddbc1d6053f'], picture: 'b3122ddbc1d6053f' },
  { id: 'venus', kind: 'planet', radiusKm: R.venus, colour: 0xe9d3a0, poleRaDeg: 272.76, poleDecDeg: 67.16, family: ['venus'], landing: ['LIB_f4e89d7f133441c7'], picture: null },
  { id: 'earth', kind: 'planet', radiusKm: R.earth, colour: 0x5b8fe0, poleRaDeg: 0, poleDecDeg: 90, family: ['earth', 'aurora'], landing: [], picture: null },
  { id: 'moon', kind: 'moon', radiusKm: R.moon, colour: 0xc9c9c9, poleRaDeg: 269.99, poleDecDeg: 66.54, family: ['moon'], landing: ['LIB_aaf6abc79e2fdc61'], picture: 'aaf6abc79e2fdc61' },
  { id: 'mars', kind: 'planet', radiusKm: R.mars, colour: 0xd8693f, poleRaDeg: 317.68, poleDecDeg: 52.89, family: ['mars'], landing: ['LIB_d7aa5ad9ff9a0207'], picture: 'd7aa5ad9ff9a0207' },
  { id: 'ceres', kind: 'dwarf', radiusKm: R.ceres, colour: 0xa8a396, poleRaDeg: 291.42, poleDecDeg: 66.76, family: ['ceres'], landing: ['LIB_e4bda39aab8ed2e7'], picture: null },
  { id: 'jupiter', kind: 'planet', radiusKm: R.jupiter, colour: 0xdcb88f, poleRaDeg: 268.06, poleDecDeg: 64.5, family: ['jupiter', 'io', 'europa', 'ganymede', 'callisto'], landing: ['LIB_9f4da4dca540f042', 'LIB_f98181e329f33c32'], picture: 'f98181e329f33c32' },
  { id: 'saturn', kind: 'planet', radiusKm: R.saturn, colour: 0xe8d197, poleRaDeg: 40.59, poleDecDeg: 83.54, family: ['saturn', 'titan', 'phoebe', 'enceladus'], landing: ['LIB_c5fdf0e040fea57c'], picture: 'c5fdf0e040fea57c' },
  { id: 'uranus', kind: 'planet', radiusKm: R.uranus, colour: 0xa3dbe3, poleRaDeg: 257.31, poleDecDeg: -15.18, family: ['uranus'], landing: ['LIB_c82e8182bd2bbf7e'], picture: 'c82e8182bd2bbf7e' },
  { id: 'neptune', kind: 'planet', radiusKm: R.neptune, colour: 0x5a7ee6, poleRaDeg: 299.36, poleDecDeg: 43.46, family: ['neptune', 'triton'], landing: ['LIB_ae8735526b833d00'], picture: 'ae8735526b833d00' },
  { id: 'pluto', kind: 'dwarf', radiusKm: R.pluto, colour: 0xd2bb9f, poleRaDeg: 132.99, poleDecDeg: -6.16, family: ['pluto', 'charon'], landing: ['LIB_f60e3e1a94427148'], picture: null },
]

const BY_ID = new Map(SOLAR_BODIES.map((b) => [b.id, b]))

export function solarBody(id: SolarBodyId): SolarBody {
  const b = BY_ID.get(id)
  if (!b) throw new Error(`unknown body ${id}`)
  return b
}

export function isSolarBodyId(v: unknown): v is SolarBodyId {
  return typeof v === 'string' && BY_ID.has(v as SolarBodyId)
}

/**
 * The orrery body a dataset's body belongs to: the body itself, or the
 * planet whose moon it is (Io → Jupiter), so a Titan row keeps Saturn's
 * list. `null` (the Earth, as offEarthBody says it) is the Earth; a body
 * the orrery does not draw (the sky, Vesta) is null.
 */
export function solarBodyOfDatasetBody(body: string | null | undefined): SolarBodyId | null {
  const name = (body ?? 'earth').trim().toLowerCase() || 'earth'
  for (const b of SOLAR_BODIES) if (b.family.includes(name)) return b.id
  return null
}
